import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { midiaService as depotService } from '@/services/midias.service'
import * as tus from 'tus-js-client'
// import { mediaFileService } from '@/services/mediafile.service'

export type UploadType = 'depotFile' | 'depotCardCover' | 'depotVideoCover' | 'tusVideo'
export type UploadStatus = 'pending' | 'uploading' | 'done' | 'error' | 'canceled' | 'processing'

type UploadStructure = {
  id: number
  type: UploadType
  relationId: string
  file: File
  progress: number
  controller?: AbortController
  tusUpload?: tus.Upload
  status?: UploadStatus
  mediaId?: string
}

export const useUploadStore = defineStore('upload', () => {
  const iteration = ref(0)

  const uploads = ref<UploadStructure[]>([])
  const carouselUploads = ref<Record<string, UploadStructure[]>>({})

  const getUploads = computed(() => (relationId: string, type: UploadType, hideComplete?: boolean) => {
    return (
      uploads.value.filter(
        (upload) =>
          upload.relationId === relationId &&
          upload.type === type &&
          ((hideComplete && upload.status !== 'done') || !hideComplete)
      ) || []
    )
  })

  /**
   * Adiciona um upload na lista
   * @param opt
   */
  const add = async (opt: {
    type?: UploadType
    relationId?: string
    file?: File
  }) => {
    opt = {
      type: undefined,
      relationId: '',
    ...opt}

    if (!opt.type) throw new Error('Type is required!')
    if (!opt.file) throw new Error('File is required!')
    if (!opt.relationId) throw new Error('RelationId is required!')

    try {
      const currentIt = iteration.value++

      // Create base upload structure
      const uploadStructure: UploadStructure = {
        id: currentIt,
        type: opt.type,
        file: opt.file,
        relationId: opt.relationId,
        progress: 0,
        status: 'pending'
      }

      uploads.value.push(uploadStructure)
      const index = uploads.value.findIndex((upload) => upload.id === currentIt)

      if (opt.type === 'tusVideo') {
        uploads.value[index].status = 'processing'
        
        // 1. Prepare Upload
        const prepareRes = await depotService.authorizeUpload(opt.file.name)

        if (!prepareRes.success || !prepareRes.data) {
           uploads.value[index].status = 'error'
           return { success: false }
        }

        return handleTusVideoUpload(
          opt.file,
          index,
          prepareRes.data
        )
      }

      const controller = new AbortController()
      uploads.value[index].controller = controller

      const extraParams = {
        signal: controller.signal,
        onUploadProgress: (progressEvent) => {
          // Update progress
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          
          if(uploads.value[index] ) {
              uploads.value[index].progress = progress
              uploads.value[index].status = 'uploading'
    
              if (progress === 100) {
                uploads.value[index].status = 'processing'
              }
          }
        }
      }

      let req = null
      if (opt.type === 'depotFile') req = depotService.fileUpload(opt.relationId, opt.file, extraParams)
      if (opt.type === 'depotCardCover') req = depotService.cardCoverUpload(opt.relationId, opt.file, extraParams)
      if (opt.type === 'depotVideoCover') req = depotService.videoCoverUpload(opt.relationId, opt.file, extraParams)

      req?.then((res) => {
        if(uploads.value[index]) uploads.value[index].status = 'done'
        return res
      }).catch((error) => {
        if(uploads.value[index]) uploads.value[index].status = 'error'
        console.error('Upload error:', error)
      })

      return req
    } catch (error) {
      // Erro
      console.log('Erro', error)
    }
  }

  // const carouselAdd = (mediaId: string, relationId: string, file: File) => {
  //   if (!mediaId) throw new Error('MediaId is required!')
  //   if (!relationId) throw new Error('RelationId is required!')
  //   if (!file) throw new Error('File is required!')

  //   const controller = new AbortController()
  //   const currentIt = iteration.value++

  //   const extraParams = {
  //     signal: controller.signal,
  //     onUploadProgress: (progressEvent) => {
  //       // Update progress
  //       const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)

  //       const index = carouselUploads.value[mediaId].findIndex((upload) => upload.id === currentIt)
  //       carouselUploads.value[mediaId][index].progress = progress
  //       carouselUploads.value[mediaId][index].status = 'uploading'

  //       if (progress === 100) {
  //         carouselUploads.value[mediaId][index].status = 'processing'
  //       }
  //     }
  //   }
  // }

  const cancel = (id: number) => {
    const idx = uploads.value.findIndex((upload) => upload.id === id)

    if (idx >= 0 && uploads.value[idx].status === 'uploading') {
      if (uploads.value[idx].type === 'tusVideo' && uploads.value[idx].tusUpload) {
        uploads.value[idx].tusUpload?.abort()
      } else if (uploads.value[idx].controller) {
        uploads.value[idx].controller?.abort()
      }
      uploads.value[idx].status = 'canceled'
    }

    return idx
  }

  /**
   * Remove um upload da lista
   * @param opt
   */
  const remove = (id: number) => {
    let idx = cancel(id) // Cancela se necessário

    if (idx === -1) idx = uploads.value.findIndex((upload) => upload.id === id)
    if (idx >= 0) uploads.value.splice(idx, 1)

    return idx
  }

  /**
   * Remove todos os uploads com o status informado
   * @param status 
   */
  const removeByStatus = (status: UploadStatus) => {
    const uploadsToRemove = uploads.value.filter((upload) => upload.status === status)
    uploadsToRemove.forEach((upload) => remove(upload.id))
  }

  /**
   * Extração do processo de TUS upload (usando o retry nativo do tus-js-client)
   */
  const handleTusVideoUpload = async (
    file: File,
    index: number,
    authData: { 
      videoId: string
      libraryId: string
      authorizationSignature: string
      authorizationExpire: number
      embedLink: string
    }
  ): Promise<any> => {
    try {
      if (!uploads.value[index]) return { success: false }

      const { videoId, libraryId, authorizationSignature, authorizationExpire, embedLink } = authData

      // 2. TUS Upload
      return await new Promise((resolve, reject) => {
        const tusUpload = new tus.Upload(file, {
          endpoint: 'https://video.bunnycdn.com/tusupload',
          retryDelays: [0, 3000, 5000, 10000, 20000],
          headers: {
            AuthorizationSignature: authorizationSignature,
            AuthorizationExpire: authorizationExpire.toString(),
            VideoId: videoId,
            LibraryId: libraryId,
          },
          metadata: {
            filetype: file.type || 'video/mp4',
            title: file.name || 'video',
          },
          onError: function (error) {
            console.error('Failed because: ' + error)
            reject(error)
          },
          onProgress: function (bytesUploaded, bytesTotal) {
            const progress = Math.round((bytesUploaded / bytesTotal) * 100)

            if (uploads.value[index]) {
              uploads.value[index].progress = progress
              uploads.value[index].status = 'uploading'
            }
          },
          onSuccess: async function () {
            await depotService.syncUpload(uploads.value[index].relationId, videoId, embedLink)

            if (uploads.value[index]) {
              uploads.value[index].status = 'done'
            }

            resolve({ success: true, videoId })
          },
        })

        if (uploads.value[index]) {
          uploads.value[index].tusUpload = tusUpload
        }

        tusUpload.start()
      })
    } catch (error) {
      console.error('Upload error:', error)

      if (uploads.value[index]) {
        uploads.value[index].status = 'error'
      }

      return { success: false }
    }
  }

  return {
    uploads,
    carouselUploads,
    getUploads,
    add,
    // carouselAdd,
    cancel,
    remove,
    removeByStatus
  }
})
